import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { loggerService } from '../../application/logger';
import { userService } from '../../application/user';
import Alert from '../../components/common/alert';
import Button from '../../components/common/button';
import LoadingSpinner from '../../components/common/loadingSpinner';
import FormContainer from '../../components/forms/formContainer';
import Input from '../../components/forms/input';
import Layout from '../../components/layout/layout';
import { AppContext, Types } from '../../context';
import { homepageRoute, routes } from '../../domain/websiteDomain';
import useUserWithRedirects from '../../hooks/useUser';

interface LoginProps {}

interface Values {
  email: string;
  password: string;
}

const schema = Yup.object({
  email: Yup.string().required('required'),
  password: Yup.string().required('required'),
});

const Login: React.FC<LoginProps> = () => {
  const { dispatch, state } = useContext(AppContext);

  const router = useRouter();
  const { target } = router.query;
  const redirectToOnSuccess =
    typeof target === 'string' ? target : homepageRoute;

  useUserWithRedirects({ redirectToIfFound: redirectToOnSuccess });

  const [alert, setAlert] = useState<string | null>(null);

  const { register, handleSubmit, errors, getValues, formState } =
    useForm<Values>({
      resolver: yupResolver(schema),
      mode: 'onTouched',
    });

  const onSubmit = handleSubmit(async (values: Values) => {
    const applicationLogger = loggerService();

    setAlert(null);
    const { email, password } = values;

    try {
      const { loginUser } = userService();

      const u = await loginUser(email, password);
      dispatch({ type: Types.SET_USER, payload: u });

      // check if query parameter is here
      if (router.query.redirect) {
        window.location.href = router.query.redirect as any;
      } else {
        router.push(redirectToOnSuccess);
      }
    } catch (err) {
      const castError = err as Error;
      applicationLogger.logErrorMessage({
        caller: 'login.handleSubmit',
        message: `Failed post authentication functions: ${castError.message}`,
      });
      setAlert('Sorry... something went wrong. Please try again.');
    }
  });

  return (
    <Layout classes="bg-background-lighter" title="Login">
      <FormContainer title="Login">
        <form onSubmit={onSubmit} className="space-y-8">
          {router.query.error === 'token_refresh_fail' && (
            <Alert status="error">
              Unfortunately your session has expired and you must re-login
            </Alert>
          )}

          <Input
            label="Email"
            type="email"
            name="email"
            ref={register}
            errors={errors}
            defaultValue={''}
          />

          <Input
            label="Password"
            type="password"
            ref={register}
            name="password"
            errors={errors}
            defaultValue={''}
          />

          {alert && <Alert status="error">{alert}</Alert>}

          <Button
            buttonStyle="primary"
            type="submit"
            classes="w-full flex items-center justify-center"
          >
            {formState.isSubmitting ? <LoadingSpinner /> : 'Login'}
          </Button>
        </form>

        <div className="flex flex-col justify-between pt-4 sm:flex-row md:text-lg">
          <div className="pb-2 sm:pb-0">
            Not a member yet?{' '}
            <span className="text-accent">
              <Link href="/user/signup">Sign up</Link>
            </span>
          </div>

          <span className="text-accent">
            <Link
              href={`${routes.FORGOT_PASSWORD}${
                getValues().email ? `?email=${getValues().email}` : ''
              }`}
            >
              Forgot Password
            </Link>
          </span>
        </div>
      </FormContainer>
    </Layout>
  );
};

export default Login;
